import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL =
    "https://dev.mithahomes.com/backend/api/mainprojectprocess/projectsgetapi";

  const safeParseJSON = (jsonString, defaultValue = []) => {
    try {
      if (!jsonString || jsonString === "null" || jsonString === "undefined")
        return defaultValue;
      if (typeof jsonString === "object") return jsonString;
      return JSON.parse(jsonString);
    } catch (err) {
      return defaultValue;
    }
  };

  const hasValidData = (array) => {
    return (
      Array.isArray(array) &&
      array.length > 0 &&
      array.some((item) => {
        return Object.values(item).some(
          (val) => val !== null && val !== undefined && val !== "",
        );
      })
    );
  };

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!id) {
        setError("No project ID provided");
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/${id}`);
        let rawData =
          response.data.data || response.data.result || response.data;
        const parsedData = {
          ...rawData,
          area_statement: safeParseJSON(rawData.area_statement),
          specification_details: safeParseJSON(rawData.specification_details),
          payment_schedule: safeParseJSON(rawData.payment_schedule),
          highcontent: safeParseJSON(rawData.highcontent),
        };
        setProjectData(parsedData);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch project details.");
        setLoading(false);
      }
    };
    fetchProjectDetails();
  }, [id]);

  // --- UNIFIED DESIGN STYLES ---
  const containerStyle = {
    fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    margin: "0 auto",
    padding: "20px",
    maxWidth: "1000px", // Slightly narrower for better readability
    color: "#000",
  };

  const mainHeaderStyle = {
    textAlign: "center",
    fontSize: "22px",
    fontWeight: "bold",
    margin: "40px 0 20px 0",
    textTransform: "none",
  };

  const tableStyle = {
    width: "100%",
    tableLayout: "fixed", // Forces consistent column sizing
    borderCollapse: "collapse",
    marginBottom: "40px",
    fontSize: "13px",
  };

  const thStyle = {
    backgroundColor: "#1a1a1a",
    color: "#ffffff",
    padding: "12px 15px",
    textAlign: "center",
    border: "1px solid #333",
    fontWeight: "bold",
  };

  const tdStyle = {
    padding: "12px 15px",
    border: "1px solid #ccc",
    textAlign: "center",
    wordBreak: "break-word",
  };

  // Special styles for the Specification section to act like a table
  const specContainerStyle = {
    width: "100%",
    display: "table",
    borderCollapse: "collapse",
    marginBottom: "40px",
  };

  const specRowStyle = {
    display: "table-row",
  };

  const specLabelStyle = {
    display: "table-cell",
    width: "30%", // Fixed width for titles
    backgroundColor: "#1a1a1a",
    color: "#fff",
    padding: "12px 15px",
    fontSize: "12px",
    fontWeight: "bold",
    border: "1px solid #333",
    textTransform: "uppercase",
    verticalAlign: "middle",
  };

  const specValueStyle = {
    display: "table-cell",
    width: "70%", // Fixed width for descriptions
    padding: "12px 15px",
    border: "1px solid #ccc",
    fontSize: "13px",
    lineHeight: "1.6",
    textAlign: "left",
    verticalAlign: "middle",
  };

  if (loading) return <div style={containerStyle}>Loading...</div>;
  if (error) return <div style={containerStyle}>{error}</div>;
  if (!projectData)
    return <div style={containerStyle}>No project data found</div>;

  return (
    <div style={containerStyle}>
      {/* 1. Area Statement */}
      {hasValidData(projectData.area_statement) && (
        <>
          <h2 style={mainHeaderStyle}>Area Statement</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Flat Number</th>
                  <th style={thStyle}>Super Plinth Area in Sq.Ft</th>
                  <th style={thStyle}>No. of BHK</th>
                </tr>
              </thead>
              <tbody>
                {projectData.area_statement.map((area, i) =>
                  !area.flat_number &&
                  !area.title &&
                  !area.total_sqft &&
                  !area.bhk ? null : (
                    <tr key={i}>
                      <td style={tdStyle}>
                        {area.flat_number || area.title || "N/A"}
                      </td>
                      <td style={tdStyle}>
                        {area.total_sqft ? `${area.total_sqft} ` : "N/A"}
                      </td>
                      <td style={tdStyle}>{area.bhk || "N/A"}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* 2. Specification */}
      {hasValidData(projectData.specification_details) && (
        <>
          <h2 style={mainHeaderStyle}>Specification</h2>
          <div style={specContainerStyle}>
            {projectData.specification_details.map((spec, i) =>
              !spec.title && !spec.content_id ? null : (
                <div key={i} style={specRowStyle}>
                  <div style={specLabelStyle}>{spec.title || "N/A"}</div>
                  <div style={specValueStyle}>{spec.content_id || "N/A"}</div>
                </div>
              ),
            )}
          </div>
        </>
      )}

      {/* 3. Payment Schedule */}
      {hasValidData(projectData.payment_schedule) && (
        <>
          <h2 style={mainHeaderStyle}>Payment Schedule</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Details</th>
                  <th style={thStyle}>Payment</th>
                </tr>
              </thead>
              <tbody>
                {projectData.payment_schedule.map((pay, i) =>
                  !pay.details && !pay.title && !pay.payment ? null : (
                    <tr key={i}>
                      <td style={tdStyle}>
                        {pay.details || pay.title || "N/A"}
                      </td>
                      <td style={tdStyle}>
                        {pay.payment ? `${pay.payment}%` : "N/A"}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PropertyDetails;
